import { Link } from "react-router-dom";
import SlotifyLogo from "@/components/SlotifyLogo";
import { useLanguage, languageOptions, Language } from "@/contexts/LanguageContext";
import { useTheme } from "next-themes";
import { Sun, Moon, Monitor } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const Footer = () => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const currentLang = languageOptions.find((l) => l.code === language)!;

  const themeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor;
  const ThemeIcon = themeIcon;

  return (
    <footer className="border-t border-border bg-card py-12">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
          <SlotifyLogo className="h-6" />
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">{t("landing.nav_features")}</a>
            <a href="#how-it-works" className="hover:text-foreground transition-colors">{t("landing.nav_how")}</a>
            <Link to="/login" className="hover:text-foreground transition-colors">{t("landing.nav_login")}</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">{t("legal.privacy_title")}</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">{t("legal.terms_title")}</Link>
          </div>
          <div className="flex items-center gap-3">
            {/* Language switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground hover:bg-muted">
                <span className="text-base leading-none">{currentLang.flag}</span>
                <span>{currentLang.label}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[140px] bg-popover border border-border shadow-md">
                {languageOptions.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={cn("gap-2 cursor-pointer", language === lang.code && "bg-muted font-medium")}
                  >
                    <span className="text-base leading-none">{lang.flag}</span>
                    <span>{lang.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger aria-label="Theme" className="flex items-center justify-center rounded-md border border-border bg-card p-1.5 text-muted-foreground transition-colors hover:text-foreground hover:bg-muted">
                <ThemeIcon className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[130px] bg-popover border border-border shadow-md">
                {[
                  { value: "light", label: t("profile.theme_light"), icon: Sun },
                  { value: "dark", label: t("profile.theme_dark"), icon: Moon },
                  { value: "system", label: t("profile.theme_system"), icon: Monitor },
                ].map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={() => setTheme(opt.value)}
                    className={cn("gap-2 cursor-pointer", theme === opt.value && "bg-muted font-medium")}
                  >
                    <opt.icon className="h-4 w-4" />
                    <span>{opt.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <p className="mt-6 text-center text-sm text-muted-foreground md:mt-4">
          {t("landing.footer_rights")}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
